import { ReactNode, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Check,
  Circle,
  Eraser,
  Gift,
  MessageSquare,
  Shield,
  Trophy,
  UserPlus,
  Users,
  XCircle,
} from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Button } from "../../components/Button/Button";
import { Modal } from "../../components/Modal/Modal";
import { Skeleton } from "../../components/Skeleton/Skeleton";

import { ApiError, NetworkError } from "../../lib/errors";
import {
  getProjectWipePreflight,
  startProjectWipe,
  type ProjectWipePreflight,
  type ProjectWipeStep,
  type ProjectWipeTask,
} from "./api";
import {
  formatDuration,
  num,
  plural,
  projectWipePreflightQueryKey,
  projectWipeQueryKey,
  taskElapsedMs,
  useNow,
  type WipeCounts,
} from "./wipe";
import styles from "./WipeProjectDialog.module.css";

interface Props {
  projectId: string;
  projectName: string;
  /**
   * The project's latest wipe task, as the page's two-second poll sees it —
   * null when the project has never been wiped. The dialog never fetches it
   * itself: the page needs the same answer for its banner, its badge and its
   * menu, and two readers of one task is how they end up disagreeing.
   */
  task: ProjectWipeTask | null;
  /**
   * Which half of the dialog the operator asked for.
   *
   * `"question"` is the menu item — offer a wipe. `"task"` is a banner on the
   * page — show me the one that is there. The second exists because a wipe
   * that stopped is still true days later: without it the stopped report would
   * be reachable only in the browser that happened to watch it die, and
   * everyone else would open the question and be handed fresh preflight
   * numbers for a project that is already half empty.
   *
   * A wipe that is RUNNING overrides this either way (below): there is nothing
   * left to ask.
   */
  view: "question" | "task";
  /**
   * What the preflight counted in the moment this wipe was confirmed. Null
   * when the page was opened after the wipe had already started — nothing on
   * the server can reconstruct those figures once the rows are gone, so the
   * report simply leaves that line out rather than guess it.
   */
  startedWith: WipeCounts | null;
  /**
   * Hands the page the figures its completion toast will repeat — and `null`
   * when the wipe that is now running has none.
   *
   * Called on every start, including the ones with nothing to hand over, so
   * that what the page holds always describes the task it is watching. The
   * cases that pass null are "Run it again", whose only counts are the ones
   * the first attempt was shown (some of those players it already deleted,
   * and the game API was open the whole time the operator thought about it),
   * and a 409, whose wipe is somebody else's.
   */
  onStarted: (counts: WipeCounts | null) => void;
  onClose: () => void;
}

/**
 * The one dialog behind "Wipe project data" — the question, and then the task
 * it starts.
 *
 * All four states live in the same `Modal`: the header stays put and only the
 * body and the footer change, because the operator who confirmed a wipe and
 * the operator watching it are the same person in the same second, and a
 * second window would make them look away to find the answer.
 *
 * Which state is drawn is decided by the task on the SERVER, not by anything
 * this component did: reopening the dialog while a wipe runs lands in the
 * progress view, because the wipe is not the dialog's — closing the window
 * does not stop it. The one thing the dialog keeps for itself is whether it
 * has been watching (below).
 */
export function WipeProjectDialog({
  projectId,
  projectName,
  task,
  view,
  startedWith,
  onStarted,
  onClose,
}: Props) {
  const qc = useQueryClient();
  const [typed, setTyped] = useState("");

  /**
   * Whether this dialog is a view of the task above.
   *
   * A running task always is — there is no question to ask about a wipe that
   * is already under way. A FINISHED one is only when the operator asked for
   * it, either by opening this from a banner (`view`) or by starting the wipe
   * in this very dialog and staying to watch. Hence state initialised once at
   * mount rather than recomputed: a wipe that ended last week is history, and
   * answering the menu with "Project wiped" would leave no way to ask for
   * another one.
   */
  const [watching, setWatching] = useState(
    () => view === "task" || task?.status === "running",
  );
  /**
   * Whether this dialog has had the wipe it reports RUNNING in front of it.
   *
   * It decides one thing: whether the stopped state's "Run it again" is a
   * single press, or asks for the project's name first (state 4 below). §7.2
   * writes that button without a confirmation and is right about the operator
   * it describes — one who watched the bar stop and is answering the report in
   * front of them. It is not right about the other way into that state: the
   * page's stopped banner holds for as long as the failed wipe is the
   * project's latest, which is weeks, and it opens straight into state 4 with
   * the preflight deliberately not fetched. One press there empties everything
   * that has accumulated since — over a project whose game API was open the
   * whole time — without a figure anywhere on the screen.
   *
   * Watching is the discriminator rather than `view`: a dialog opened from the
   * RUNNING banner is `view: "task"` as well, and that operator saw the wipe
   * stop with their own eyes a second ago. What separates the safe press from
   * the dangerous one is elapsed time and knowledge, not which button opened
   * the window.
   *
   * Set exactly where `watching` is, and never from an effect: those three
   * moments — mounting onto a running task, starting one here, adopting the
   * one a 409 pointed at — are every way this dialog ever comes to be looking
   * at a wipe in flight.
   */
  const [sawItRun, setSawItRun] = useState(() => task?.status === "running");
  const shown = watching ? task : null;

  const preflight = useQuery({
    queryKey: projectWipePreflightQueryKey(projectId),
    queryFn: () => getProjectWipePreflight(projectId),
    // Only the question asks, and only the question may: counting rows during
    // a wipe returns a project halfway gone, which is nobody's answer to
    // "what will this remove".
    enabled: !shown,
    // A preflight is a snapshot of a moment, not a cache entry: reopening the
    // dialog asks again.
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });

  const counts: WipeCounts | null = preflight.data
    ? { players: preflight.data.players, guilds: preflight.data.guilds }
    : null;

  const start = useMutation({
    mutationFn: () => startProjectWipe(projectId),
    onSuccess: (started) => {
      // Seed the page's poll with the task we just got instead of waiting for
      // its next tick: the banner, the badge and the greyed-out menu all read
      // that query, and for two seconds they would have said the project was
      // idle while this dialog showed a progress bar.
      qc.setQueryData(projectWipeQueryKey(projectId), started);
      // `shown` is the task this dialog was already looking at, so a POST that
      // succeeded with one on screen is "Run it again" — and the preflight in
      // hand belongs to the attempt before it. It is still in the query cache
      // (the observer is mounted, `enabled` only stopped the fetching), which
      // is exactly why the decision is made from the state and not from
      // whether `counts` happens to be there: those figures were counted
      // before a wipe that has since deleted some of them, with the game API
      // open ever since. Nothing is the honest answer, and the report and the
      // toast both have a shape for it.
      onStarted(shown ? null : counts);
      setWatching(true);
      setSawItRun(true);
    },
    onError: (err) => {
      // 409 — a wipe was already running: another tab, another operator. The
      // partial unique index refused the second one, and there is nothing left
      // to confirm, so the dialog becomes a view of the task that won as soon
      // as the refetch below lands.
      if (err instanceof ApiError && err.code === "wipe_in_progress") {
        // Whatever this dialog counted, it counted for a wipe that never ran.
        // The one it is about to show belongs to somebody else, started at a
        // moment this browser knows nothing about.
        onStarted(null);
        setWatching(true);
        // Somebody else's wipe, but running, and this dialog is about to show
        // it running — the operator watches this one stop like any other.
        setSawItRun(true);
        // Exact: the preflight's key sits under this one, and a wipe that is
        // already running is the last moment to go counting rows again.
        void qc.invalidateQueries({
          queryKey: projectWipeQueryKey(projectId),
          exact: true,
        });
      }
    },
  });

  const now = useNow(shown?.status === "running");

  /**
   * The footer's Close, and where the keyboard is put when the dialog changes
   * shape under it.
   *
   * Pressing "Wipe project data" disables that button the same instant
   * (`loading`), so the browser drops focus to <body>; a moment later the
   * question's footer unmounts and the running one takes its place. `Modal`
   * seats focus once, in an effect keyed on `open`, which does not change
   * here — and its Tab trap only intercepts when the active element is the
   * dialog's own first or last focusable, so from <body> the browser's default
   * applies and the next Tab lands on the page BEHIND a dialog that is
   * portalled last. Every operator passes through this transition; the keys on
   * the footer buttons save only the ones that follow it.
   *
   * Done here rather than by teaching `Modal` to re-seat focus whenever its
   * footer changes identity: every dialog in the panel would inherit a rule
   * about a problem one of them has, and `Modal` could only ever guess at the
   * first focusable, while this component knows exactly which button it just
   * drew. What is deliberately not done is stealing focus that is already
   * inside the dialog — running → stopped keeps the operator on the Close they
   * were waiting on, which is what the footer keys are for.
   */
  const closeRef = useRef<HTMLButtonElement>(null);
  const seated = useRef(false);
  const footerShape = shown ? shown.status : "question";
  useEffect(() => {
    // The first footer is `Modal`'s to focus — it prefers the body's first
    // control, which in the question is the field the name goes into.
    if (!seated.current) {
      seated.current = true;
      return;
    }
    const close = closeRef.current;
    if (!close) return;
    if (close.closest('[role="dialog"]')?.contains(document.activeElement)) {
      return;
    }
    close.focus();
  }, [footerShape]);

  // Trimmed on both sides: the name is meant to be copied out of the line
  // above, and a paste that carries a trailing space is not a different
  // project. A blank name confirms nothing — an empty field would otherwise
  // equal it and arm the button with nothing typed at all.
  const wantedName = projectName.trim();
  const confirmed = wantedName !== "" && typed.trim() === wantedName;
  const canWipe = confirmed && !!preflight.data && !start.isPending;

  let title = "Wipe project data";
  let icon: ReactNode = <Eraser size={17} aria-hidden="true" />;
  let iconTone: "danger" | "success" = "danger";
  let body: ReactNode;
  let footer: ReactNode;

  if (!shown) {
    // === 1. The question =============================================== //
    body = (
      <>
        <p className={styles.modalBodyText}>
          Every player, guild and bot in <b>{projectName}</b> goes for good,
          along with everything they made. The project and its settings stay.{" "}
          <b>This cannot be undone.</b>
        </p>

        {preflight.isError ? (
          <>
            <Alert variant="danger">
              Couldn&apos;t count what this wipe removes. Nothing has been
              touched — try again.
            </Alert>
            <div className={styles.retry}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void preflight.refetch()}
                loading={preflight.isFetching}
              >
                Retry
              </Button>
            </div>
          </>
        ) : preflight.data ? (
          <PreflightBreakdown preflight={preflight.data} />
        ) : (
          <PreflightSkeleton />
        )}

        <ConfirmName
          projectName={projectName}
          typed={typed}
          confirmed={confirmed}
          onType={setTyped}
        />

        <StartFailure error={start.isError ? start.error : null} />
      </>
    );
    // Every footer button below is keyed by what it DOES, and that is the whole
    // point of the keys: all four states hand `Modal` one footer, and React
    // matches its children by position. Unkeyed, the running state's "Close"
    // and the stopped state's "Run it again" are the same slot filled by the
    // same component, so the <button> is updated in place — the operator who
    // was waiting out the wipe with focus on "Close" (the modal's Tab trap has
    // exactly two stops in that state: the header × and this one) finds their
    // Enter sitting on the button that starts a second wipe, over a task that
    // is already `failed` and a partial unique index that is free again.
    //
    // Keyed, "close" is only ever matched by "close": focus survives the state
    // change without the button changing meaning, and "rerun" arrives as a new
    // node nothing is pointed at. The alternative — one key on each state's
    // footer — fixes the same thing by rebuilding both buttons on every
    // transition, and throws the operator's focus back to <body> each time the
    // wipe moves on.
    footer = (
      <>
        <Button
          key="cancel"
          variant="secondary"
          onClick={onClose}
          disabled={start.isPending}
        >
          Cancel
        </Button>
        <Button
          key="wipe"
          variant="danger"
          onClick={() => start.mutate()}
          disabled={!canWipe}
          loading={start.isPending}
        >
          Wipe project data
        </Button>
      </>
    );
  } else if (shown.status === "running") {
    // === 2. It is running ============================================== //
    title = "Wiping project data";
    icon = <span className={styles.spin} />;
    body = (
      <>
        <ProgressBar percent={shown.percent} />
        <div className={styles.barMeta}>
          <span>
            <span className={styles.mono}>{num(shown.rows_deleted)}</span>{" "}
            {plural(shown.rows_deleted, "row")} removed
          </span>
          <span className={styles.mono}>
            {formatDuration(taskElapsedMs(shown, now))}
          </span>
        </div>
        <StepList steps={shown.steps} taskStatus={shown.status} />
      </>
    );
    footer = (
      <>
        <span className={styles.note}>Closing this window won&apos;t stop it.</span>
        <Button key="close" ref={closeRef} variant="secondary" onClick={onClose}>
          Close
        </Button>
      </>
    );
  } else if (shown.status === "done") {
    // === 3. It finished ================================================ //
    title = "Project wiped";
    icon = <Check size={17} aria-hidden="true" />;
    iconTone = "success";
    body = (
      <>
        <p className={styles.modalBodyText}>
          <b>{projectName}</b> is empty and open for business again. Settings,
          leaderboards and bot phrases are where you left them.
        </p>
        <div className={styles.section}>
          <div className={styles.label}>Removed</div>
          <div className={styles.list}>
            {/* Only when this browser asked the question that started the
                wipe. Nothing left on the server counts players and guilds
                separately after the fact — the task reports rows per step —
                and a figure invented here would be a lie about a deletion. */}
            {startedWith && (
              <Row icon={<Check size={14} />} tone="ok">
                <span className={styles.num}>{num(startedWith.players)}</span>{" "}
                {plural(startedWith.players, "player")} and{" "}
                <span className={styles.num}>{num(startedWith.guilds)}</span>{" "}
                {plural(startedWith.guilds, "guild")}
              </Row>
            )}
            <Row icon={<Check size={14} />} tone="ok">
              <span className={styles.num}>{num(shown.rows_deleted)}</span>{" "}
              {plural(shown.rows_deleted, "row")} in total, in{" "}
              <span className={styles.num}>
                {formatDuration(taskElapsedMs(shown, now))}
              </span>
            </Row>
          </div>
        </div>
      </>
    );
    footer = (
      <Button key="close" ref={closeRef} variant="secondary" onClick={onClose}>
        Close
      </Button>
    );
  } else {
    // === 4. It stopped ================================================= //
    title = "Wipe stopped";
    icon = <AlertTriangle size={17} aria-hidden="true" />;
    body = (
      <>
        <ProgressBar percent={shown.percent} failed />
        <div className={styles.barMeta}>
          <span>
            <span className={styles.mono}>{num(shown.rows_deleted)}</span>{" "}
            {plural(shown.rows_deleted, "row")} removed
          </span>
          <span className={styles.mono}>stopped at {shown.percent}%</span>
        </div>
        <StepList steps={shown.steps} taskStatus={shown.status} />
        <div className={styles.alertGap}>
          <Alert variant="danger" title="The project is half empty">
            Nothing was rolled back — what you see above is already gone. The
            game API is open again. Running the wipe once more picks up where it
            stopped.
          </Alert>
        </div>
        {/* Asked of the operator who did not watch this wipe stop — see
            `sawItRun`. The same block the question uses, down to the copy: the
            gate on the panel's most destructive button is one thing, and a
            second wording of it would be a second thing to keep true. */}
        {!sawItRun && (
          <ConfirmName
            projectName={projectName}
            typed={typed}
            confirmed={confirmed}
            onType={setTyped}
          />
        )}
        {/* The same plaque as the question's: "Run it again" is the same POST,
            and a second attempt that fails silently is read as a button that
            does nothing. */}
        <StartFailure error={start.isError ? start.error : null} />
      </>
    );
    footer = (
      <>
        <Button
          key="close"
          ref={closeRef}
          variant="secondary"
          onClick={onClose}
          // The same second the × greys out, and for the same reason: while
          // the retry POST is in flight `closeDisabled` has already taken
          // Escape, the backdrop and the × away, and a way out that is refused
          // but still looks live is not an answer (see `Modal.closeDisabled`).
          // The question's "Cancel" beside the same POST does exactly this.
          disabled={start.isPending}
        >
          Close
        </Button>
        <Button
          key="rerun"
          variant="danger"
          onClick={() => start.mutate()}
          disabled={!sawItRun && !confirmed}
          loading={start.isPending}
        >
          Run it again
        </Button>
      </>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      // While the POST is in flight there is no way out: the operation is not
      // idempotent from the operator's side, and a second attempt is not
      // something to leave one keystroke away. Once it is running the exits
      // come back — the footer says why closing is safe.
      closeDisabled={start.isPending}
      title={title}
      width="sm"
      icon={icon}
      iconTone={iconTone}
      footer={footer}
    >
      {/* The state the dialog has just moved into, said out loud.
          `Modal` carries `title` in the dialog's `aria-label`, and a label that
          changes under an already-open dialog is announced by nothing: a screen
          reader reads it when focus enters and never again. Every state change
          here happens without focus moving — the task is the server's, and it
          reports on its own — so the heading is repeated in a live region,
          which is read whenever it changes.
          Two of the three changes are otherwise silent: the wipe starting, and
          the wipe finishing. The third, a wipe that stopped, does announce
          itself through the `role="alert"` on its "The project is half empty"
          plaque — but as the consequence, not as the state, and one state
          change out of three arriving is worse than none for anyone trying to
          learn what this window does.
          Hidden rather than drawn, because the heading is already on screen a
          line above and the mockup's window has one of it. */}
      <p className={styles.srOnly} role="status">
        {title}
      </p>
      {body}
    </Modal>
  );
}

/**
 * Why the wipe did not start.
 *
 * A POST that fails leaves the dialog exactly as it was, which on the most
 * destructive button in the panel reads as "it didn't work" — and the answer
 * to that is to press it again. So each failure says what is, and what is not,
 * known about the project's state.
 *
 * The 409 draws nothing: it is not a failure but somebody else's wipe, and the
 * dialog has already turned into a view of that one.
 */
function StartFailure({ error }: { error: unknown }) {
  if (!error) return null;
  if (error instanceof ApiError && error.code === "wipe_in_progress") {
    return null;
  }
  return (
    <div className={styles.errorBlurb} role="alert">
      {error instanceof NetworkError ? (
        // No promise about what happened: the request may have been carried
        // out and only the answer lost on the way back.
        <>
          Couldn&apos;t reach Social Hub API, so there is no telling whether the
          wipe started. Reload the page — if it did, this dialog shows it
          running.
        </>
      ) : (
        <>
          Couldn&apos;t start the wipe. Nothing was deleted — the project is as
          it was.
        </>
      )}
    </div>
  );
}

/**
 * The gate on every wipe this dialog starts: the project's name, typed out.
 *
 * One component for both states that ask for it — the question, and a stopped
 * wipe nobody here watched stop — because the words and the two visual states
 * below are the mockup's, and a second copy of them is how one screen ends up
 * saying something the other does not.
 */
function ConfirmName({
  projectName,
  typed,
  confirmed,
  onType,
}: {
  projectName: string;
  typed: string;
  confirmed: boolean;
  onType: (next: string) => void;
}) {
  // Nothing to retype, so there is no gate to pass — and a gate that passes
  // itself is worse than none: the button would arm on an empty field. Only
  // the internal ops route can create a project without a name, so this is a
  // dead end nobody should meet; it still says what it is rather than showing
  // an empty chip above a field that never confirms.
  if (projectName.trim() === "") {
    return (
      <div className={styles.confirm}>
        <span className={styles.helper}>
          This project has no name, and the wipe is confirmed by typing it.
          Give it one in the project&apos;s settings first.
        </span>
      </div>
    );
  }

  return (
    <div className={styles.confirm}>
      {/* A <span>, not a <label htmlFor>: Chrome's label activation fires on
          mouseup inside a label and collapses the selection of the very name
          that is here to be copied. `aria-label` names the field instead — as
          in DeleteProjectModal, RotateConfirmModal and ApplyConfirmModal. */}
      <span className={styles.modalLabel}>
        Type <b>{projectName}</b> to confirm
      </span>
      {/* The two states the mockup names: the project's name sitting faint in
          an empty field, and the accent border the moment what is typed IS
          that name. `confirmed` and not focus decides, so the ring says "this
          is the right project" rather than "the caret is here" — which on a
          dialog that opens with the caret already here would be no signal at
          all. */}
      <input
        className={`${styles.input} ${confirmed ? styles.match : ""}`}
        aria-label={`Type ${projectName} to confirm`}
        placeholder={projectName}
        autoComplete="off"
        spellCheck={false}
        value={typed}
        onChange={(e) => onType(e.target.value)}
      />
      <span className={styles.helper}>The name has to match exactly.</span>
    </div>
  );
}

// === The question's breakdown ========================================== //

/**
 * What goes, and — the reason this operation exists at all — what stays.
 *
 * The second section is as long as the first on purpose: the button next to
 * this one deletes the project itself, and an operator who is not shown the
 * survivors cannot tell the two apart, so they press neither. Every row is
 * drawn whatever its count says: this is the plan of the wipe, not a list of
 * consequences that vary, and a project with no guilds left is still a project
 * whose guilds this removes.
 */
function PreflightBreakdown({ preflight }: { preflight: ProjectWipePreflight }) {
  return (
    <>
      <div className={styles.section}>
        <div className={styles.label}>What goes</div>
        <div className={styles.list}>
          <Row icon={<Users size={14} />}>
            <span className={styles.num}>{num(preflight.players)}</span>{" "}
            {plural(preflight.players, "player")},{" "}
            <span className={styles.num}>{num(preflight.bots)}</span>{" "}
            {/* Not `plural` — "1 of them bot" is not what the singular of
                "N of them bots" is. The appositive takes an article instead. */}
            of them {preflight.bots === 1 ? "a bot" : "bots"}, with their linked
            accounts
          </Row>
          <Row icon={<Shield size={14} />}>
            <span className={styles.num}>{num(preflight.guilds)}</span>{" "}
            {plural(preflight.guilds, "guild")} with their rosters, invites,
            applications and block lists
          </Row>
          <Row icon={<MessageSquare size={14} />}>
            <span className={styles.num}>{num(preflight.chat_messages)}</span>{" "}
            chat {plural(preflight.chat_messages, "message")}
          </Row>
          {/* Gifts ride on the help row rather than a row of their own: they
              are the push half of the mechanic donations are the pull half of,
              and an operator reading this list is counting the same kind of
              loss twice over, not two features. */}
          <Row icon={<Gift size={14} />}>
            <span className={styles.num}>{num(preflight.help_requests)}</span>{" "}
            help {plural(preflight.help_requests, "request")} and the{" "}
            <span className={styles.num}>{num(preflight.help_donations)}</span>{" "}
            {plural(preflight.help_donations, "donation")} on them, plus{" "}
            <span className={styles.num}>{num(preflight.gifts)}</span>{" "}
            {plural(preflight.gifts, "gift")} sent player to player
          </Row>
          <Row icon={<UserPlus size={14} />}>
            <span className={styles.num}>{num(preflight.friendships)}</span>{" "}
            {plural(preflight.friendships, "friendship")} and{" "}
            <span className={styles.num}>{num(preflight.friend_requests)}</span>{" "}
            pending {plural(preflight.friend_requests, "request")}
          </Row>
          <Row icon={<Trophy size={14} />}>
            <span className={styles.num}>
              {num(preflight.leaderboard_cycles)}
            </span>{" "}
            leaderboard {plural(preflight.leaderboard_cycles, "cycle")} holding{" "}
            <span className={styles.num}>
              {num(preflight.leaderboard_scores)}
            </span>{" "}
            {plural(preflight.leaderboard_scores, "score")} and{" "}
            <span className={styles.num}>
              {num(preflight.leaderboard_reward_grants)}
            </span>{" "}
            granted {plural(preflight.leaderboard_reward_grants, "reward")}
          </Row>
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.label}>What stays</div>
        <div className={styles.list}>
          <Row muted tone="ok" icon={<Check size={14} />}>
            Project configuration — chat, guild defaults, help, rate limits,
            auth
          </Row>
          {/* The one place the border is not obvious, so it is drawn in words:
              a board is configuration, its cycles and scores are a trace of
              players. */}
          <Row muted tone="ok" icon={<Check size={14} />}>
            <span className={styles.num}>{num(preflight.leaderboards)}</span>{" "}
            {plural(preflight.leaderboards, "leaderboard")} with their reward
            tables — the boards empty out, they don&apos;t disappear
          </Row>
          <Row muted tone="ok" icon={<Check size={14} />}>
            <span className={styles.num}>{num(preflight.bot_phrases)}</span> bot{" "}
            {plural(preflight.bot_phrases, "phrase")} and the simulation
            settings
          </Row>
          <Row muted tone="ok" icon={<Check size={14} />}>
            API keys, team members and the activity log
          </Row>
        </div>
      </div>

      <div className={styles.section}>
        <Alert variant="warn" title="The game API closes while this runs">
          Players get <code>503</code>, and the tokens they hold stop working
          for good — the accounts behind them won&apos;t exist. They sign in
          again on a clean account.
        </Alert>
      </div>
    </>
  );
}

/** The dialog's shape while the preflight is in flight. */
function PreflightSkeleton() {
  return (
    <>
      <div className={styles.section}>
        <div className={styles.label}>What goes</div>
        <div className={styles.list}>
          <Skeleton width="86%" height={12} />
          <Skeleton width="78%" height={12} />
          <Skeleton width="52%" height={12} />
          <Skeleton width="80%" height={12} />
        </div>
      </div>
      <div className={styles.section}>
        <div className={styles.label}>What stays</div>
        <div className={styles.list}>
          <Skeleton width="74%" height={12} />
          <Skeleton width="66%" height={12} />
        </div>
      </div>
    </>
  );
}

function Row({
  icon,
  muted,
  tone,
  children,
}: {
  icon: ReactNode;
  muted?: boolean;
  tone?: "ok";
  children: ReactNode;
}) {
  return (
    <div className={`${styles.item} ${muted ? styles.muted : ""}`}>
      <span
        className={`${styles.ico} ${tone === "ok" ? styles.ok : ""}`}
        aria-hidden
      >
        {icon}
      </span>
      <span>{children}</span>
    </div>
  );
}

// === The task ========================================================== //

function ProgressBar({
  percent,
  failed,
}: {
  percent: number;
  failed?: boolean;
}) {
  const clamped = Math.max(0, Math.min(100, percent));
  return (
    <div
      className={`${styles.bar} ${failed ? styles.failed : ""}`}
      role="progressbar"
      aria-label="Wipe progress"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <i style={{ width: `${clamped}%` }} />
    </div>
  );
}

/**
 * The seven steps, exactly as the server reports them: the freeze, the five
 * groups of the deletion plan, the finish. The list is not filtered down to
 * what has happened — a wipe that stopped is read by what it had left to do.
 *
 * With one correction, which the server cannot make for itself: a step still
 * marked `running` inside a task that has stopped (see `interrupted` below).
 */
function StepList({
  steps,
  taskStatus,
}: {
  steps: ProjectWipeStep[];
  taskStatus: ProjectWipeTask["status"];
}) {
  const live = taskStatus === "running";
  return (
    <div className={styles.steps}>
      {steps.map((step) => {
        /**
         * The step the wipe was inside when the process died.
         *
         * `SaveProgress` is guarded on `status = 'running'` and a killed
         * process writes nothing at all, so the last word such a task ever
         * managed was "this step is running" — and the reaper that marks the
         * task failed cannot go back and correct it. The server is not able to
         * fix this; the reader is.
         *
         * It borrows `.step.fail` rather than earning a fourth kind: the
         * mockup's failure card draws exactly three (done / fail / todo) and
         * nothing that moves, and a colour it never uses would be a new word
         * in the vocabulary for a case one word already covers — this is where
         * the wipe stopped. What is NOT borrowed is the count column's
         * "failed", because that is a claim about the step and only the server
         * may make it. A step that failed threw something, and the task's
         * `error` says what. A step that was interrupted may have been working
         * perfectly when the node went away, and its row count is merely the
         * last figure anyone wrote down. Telling the operator "Guilds — failed"
         * would send them hunting through guild data for a fault that was never
         * there.
         */
        const interrupted = !live && step.state === "running";
        const drawn = interrupted ? "failed" : step.state;
        return (
          <div key={step.key} className={`${styles.step} ${styles[drawn]}`}>
            <span className={styles.sIco} aria-hidden>
              <StepIcon state={drawn} />
            </span>
            <span className={styles.sName}>{step.label}</span>
            <span className={styles.sCount}>
              {interrupted ? "stopped" : stepCount(step)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function StepIcon({ state }: { state: ProjectWipeStep["state"] }) {
  if (state === "done") return <Check size={14} />;
  if (state === "running") return <span className={styles.spin} />;
  if (state === "failed") return <XCircle size={14} />;
  return <Circle size={12} />;
}

/**
 * What the right-hand column of a step says.
 *
 * A number when the step has removed rows, and a word when it never will:
 * closing the API and reopening it are steps with nothing to count, and
 * printing "0" beside them would read as a step that found nothing to do.
 */
function stepCount(step: ProjectWipeStep): string {
  if (step.state === "failed") return "failed";
  if (step.state === "todo") return "—";
  if (step.rows > 0) return num(step.rows);
  return step.state === "done" ? "done" : "—";
}
